import { ChatFilterState, StreamUserRole, UseChatFiltersReturn } from '@features/components/Chat/types';
import { ClinicalItemType } from '@/api/apis/case';
import { StreamChannelType } from '@/api/apis/chat';

export const STREAM_ADMIN_ROLES: StreamUserRole[] = [
  StreamUserRole.CustomerAdmin,
  StreamUserRole.Moderator,
  StreamUserRole.QuoteApprover,
  StreamUserRole.SeniorLogisticManager,
  StreamUserRole.Administrator,
  StreamUserRole.NationalCsManagement,
  StreamUserRole.ClinicalCoordinatorManagement,
  StreamUserRole.ClinicalAuditor,
];

export const CONNECT_CHAT_TYPES = [
  StreamChannelType.General,
  StreamChannelType.Tmdx,
  StreamChannelType.OrganManagement,
  StreamChannelType.Quote,
  StreamChannelType.Logistics,
  StreamChannelType.OpoGeneral,
];

export const CP_CHAT_TYPES = [StreamChannelType.General, StreamChannelType.OpoGeneral, StreamChannelType.Quote];

export const OPO_CHAT_TYPES = [StreamChannelType.OpoGeneral];

export enum StreamChatMessageAction {
  Edit = 'edit',
  Delete = 'delete',
  Mute = 'mute',
  Quote = 'quote',
  React = 'react',
  Reply = 'reply',
  Pin = 'pin',
  Flag = 'flag',
}

export const STREAM_CHAT_MESSAGE_ACTIONS: StreamChatMessageAction[] = [
  StreamChatMessageAction.Edit,
  StreamChatMessageAction.Delete,
  StreamChatMessageAction.Mute,
  StreamChatMessageAction.Quote,
  StreamChatMessageAction.React,
  StreamChatMessageAction.Reply,
  StreamChatMessageAction.Pin,
  StreamChatMessageAction.Flag,
];

export enum ChatAccess {
  full,
  memberIn,
}

export const ChatAccessByRole = {
  [StreamUserRole.Moderator]: ChatAccess.full,
  [StreamUserRole.CustomerAdmin]: ChatAccess.full,
  [StreamUserRole.Customer]: ChatAccess.memberIn,
  [StreamUserRole.CustomerAdminAuditor]: ChatAccess.full,
  [StreamUserRole.User]: ChatAccess.memberIn,

  [StreamUserRole.QuoteApprover]: ChatAccess.full,
  [StreamUserRole.SeniorLogisticManager]: ChatAccess.full,
  [StreamUserRole.Administrator]: ChatAccess.full,
  [StreamUserRole.NationalCsManagement]: ChatAccess.full,
  [StreamUserRole.ClinicalCoordinatorManagement]: ChatAccess.full,
  [StreamUserRole.ClinicalAuditor]: ChatAccess.full,
  [StreamUserRole.SuperAdmin]: ChatAccess.full,
  [StreamUserRole.Section16Officer]: ChatAccess.full,
  [StreamUserRole.LogisticsTeamManagement]: ChatAccess.full,
  [StreamUserRole.OMS]: ChatAccess.full,
  [StreamUserRole.TechSupportAdmin]: ChatAccess.full,
  [StreamUserRole.OrganLeadership]: ChatAccess.full,
} as const;

export const HIDDEN_CHAT_ROLES = new Set<StreamUserRole>([
  StreamUserRole.QuoteApprover,
  StreamUserRole.SeniorLogisticManager,
  StreamUserRole.Administrator,
  StreamUserRole.NationalCsManagement,
  StreamUserRole.ClinicalCoordinatorManagement,
  StreamUserRole.ClinicalAuditor,
  StreamUserRole.SuperAdmin,
  StreamUserRole.TechSupportAdmin,
  StreamUserRole.Section16Officer,
  StreamUserRole.LogisticsTeamManagement,
]);

export enum AccessRule {
  All = 'all',
  Assigned = 'assigned',
  None = 'none',
}

export const ROLE_NOTIFICATION_RULES: Partial<Record<StreamUserRole, Partial<Record<StreamChannelType, AccessRule>>>> =
  {
    [StreamUserRole.QuoteApprover]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.SeniorLogisticManager]: {
      [StreamChannelType.Tmdx]: AccessRule.Assigned,
      [StreamChannelType.Quote]: AccessRule.Assigned,
      [StreamChannelType.Logistics]: AccessRule.All,
      [StreamChannelType.OrganManagement]: AccessRule.All,
      [StreamChannelType.General]: AccessRule.None,
    },
    [StreamUserRole.Administrator]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.NationalCsManagement]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.ClinicalCoordinatorManagement]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.ClinicalAuditor]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.SuperAdmin]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.Section16Officer]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.LogisticsTeamManagement]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.CustomerAdminAuditor]: {
      [StreamChannelType.General]: AccessRule.None,
      [StreamChannelType.Tmdx]: AccessRule.None,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.None,
    },
    [StreamUserRole.OMS]: {
      [StreamChannelType.General]: AccessRule.Assigned,
      [StreamChannelType.Tmdx]: AccessRule.Assigned,
      [StreamChannelType.Quote]: AccessRule.None,
      [StreamChannelType.Logistics]: AccessRule.None,
      [StreamChannelType.OrganManagement]: AccessRule.Assigned,
    },
  };

export enum StreamChatMessageType {
  Regular = 'regular',
  Deleted = 'deleted',
}

export const STREAM_MESSAGE_FIELD_PINNED = 'pinned';

export const CHANNEL_ACTIONS = {
  MUTE: 'Mute Channel',
  UNMUTE: 'Unmute Channel',
  PIN_CHAT: 'Pin Chat',
  UNPIN_CHAT: 'Unpin Chat',
  OPEN_MEMBERS_PANEL: 'Open Members Panel',
  CLOSE_MEMBERS_PANEL: 'Close Members Panel',
  OPEN_PINNED_PANEL: 'Open Pinned Messages',
  CLOSE_PINNED_PANEL: 'Close Pinned Messages',
};

export enum StreamEventType {
  NOTIFICATION_MESSAGE_NEW = 'notification.message_new',
  NOTIFICATION_MARK_READ = 'notification.mark_read',
  NOTIFICATION_CHANNEL_MUTES_UPDATED = 'notification.channel_mutes_updated',
  MESSAGE_NEW = 'message.new',
  MESSAGE_UPDATED = 'message.updated',
  MESSAGE_DELETED = 'message.deleted',
  MESSAGE_READ = 'message.read',
  PROCESSED = 'processed',
  ADDED_TO_CHANNEL = 'notification.added_to_channel',
  REMOVED_FROM_CHANNEL = 'notification.removed_from_channel',
  CHANNEL_UPDATED = 'channel.updated',
  MEMBER_UPDATED = 'member.updated',
  ALL = 'all',
}

export enum NotificationPermission {
  DEFAULT = 'default',
  GRANTED = 'granted',
  DENIED = 'denied',
}

export const NOTIF_CHANNEL = 'chat_notifications_channel';

export const DEFAULT_CHAT_NAME = 'Unnamed Chat';
export const DEFAULT_USER_NAME = 'Someone';
export const DEFAULT_MESSAGE_TEXT = 'New message';
export const SHARED_DATA_HEADER = 'Shared data';

export enum CustomMessageField {
  ClinicalDataAttachment = 'clinicalDataAttachment',
  QuoteAttachment = 'quoteAttachment',
  OrganApprovalAttachment = 'organApprovalAttachment',
}
export const AVAILABLE_CHANNELS_TO_SHARE: Record<CustomMessageField, StreamChannelType> = {
  [CustomMessageField.ClinicalDataAttachment]: StreamChannelType.General,
  [CustomMessageField.QuoteAttachment]: StreamChannelType.Quote,
  [CustomMessageField.OrganApprovalAttachment]: StreamChannelType.General,
};
export const CHANNEL_NAMES: Record<StreamChannelType, string> = {
  [StreamChannelType.General]: 'General',
  [StreamChannelType.Tmdx]: 'Tmdx',
  [StreamChannelType.OrganManagement]: 'Organ management',
  [StreamChannelType.Quote]: 'Quote',
  [StreamChannelType.Logistics]: 'Logistics',
  [StreamChannelType.OpoGeneral]: 'OPO',
};
export const SHARED_MESSAGE_KEYS: CustomMessageField[] = Object.values(CustomMessageField);

export const DEFAULT_CHAT_FILTER_STATE: ChatFilterState = {
  organTypes: [],
  donorTypes: [],
  chatTypes: [],
  unreadOnly: false,
};

export const DEFAULT_CHAT_FILTERS: UseChatFiltersReturn = {
  draftFilters: { ...DEFAULT_CHAT_FILTER_STATE },
  filters: { ...DEFAULT_CHAT_FILTER_STATE },
  version: 0,
  setOrganTypes: () => {},
  setDonorTypes: () => {},
  setChatTypes: () => {},
  toggleUnreadOnly: () => {},
  applyFilters: () => {},
  setAndApplyFilter: () => {},
  clearAll: () => {},
};

export const CHAT_ERROR_MESSAGES = {
  frozen: 'Sorry, this channel has been frozen by the admin',
  general: 'Something went wrong',
  chatNotFound: (channelName: string) => `${channelName} chat was not found`,
  failedToShare: 'Failed to share message.',
};

export const CHAT_SUCCESS_MESSAGES = {
  share: (channelName: string) => `Message sent to ${channelName}`,
};

export const CHAT_LABEL_UNKNOWN_USER = 'Unknown User';
export const CHAT_LABEL_UNKNOWN_CHAT = 'Unknown Chat';
export const CHAT_LABEL_PINNED_MESSAGES = 'Pinned Messages';
export const CHAT_LABEL_CHAT_MEMBERS = 'Chat Members';
export const CHAT_LABEL_CHAT_DETAILS = 'Chat Details';
export const CHAT_LABEL_EMPTY_CHANNEL_LIST = 'No chats in this case yet';
export const CHAT_LABEL_EMPTY_MESSAGE_LIST = 'No messages in this chat yet';
export const CHAT_LABEL_SEARCH_PLACEHOLDER = 'Search';
export const CHAT_LABEL_NO_CHATS_FOUND = 'No chats found';
export const CHAT_LABEL_LOADING_CHATS = 'Loading';
export const CHAT_LABEL_LUNGS_ON_OCS_WARNING =
  'For Lung cases, “On OCS Video” is restricted by default. Do you want to share it to chats anyway?';
export const CHAT_LABEL_NO_PINNED_MESSAGES = (chatName: string) => `No pinned messages in ${chatName}`;
export const CHAT_LABEL_WARNING_MODAL_BODY = (chatName: string) =>
  `Are you sure you want to share this in the external chat '${chatName}'? The data you are about to share is intended for internal use only.`;
export const CHAT_LABEL_CANCEL = 'Cancel';
export const CHAT_LABEL_WARNING_MODAL_CONFIRM = 'Share anyway';
export const CHAT_LABEL_SHARE_MODAL_TITLE = 'Share in Chat';
export const CHAT_LABEL_SHARE_MODAL_NO_OPEN_CHATS = 'No open chats found.';
export const CHAT_LABEL_SHARE_MODAL_SENDING = 'Sending...';
export const CHAT_LABEL_SHARE_MODAL_BUTTON_FALLBACK = (channelId: string) => `Channel: ${channelId}`;
export const CHAT_LABEL_FAILED_TO_SHARE_MESSAGE = 'Failed to share message.';

export const ORGAN_FEEDBACK_EMAILS: Record<string, string> = {
  Heart: 'OCSHeart@transmedics.com',
  Lung: 'OCSLung@transmedics.com',
  Liver: 'OCSLiver@transmedics.com',
};

export const DEFAULT_FEEDBACK_EMAIL = 'support@transmedics.com';

const CLOSED_CHAT_BASE =
  'Service Completed and the chats are closed! Please help us improve by providing feedback via email';

export const buildReadOnlyChatMessage = () => "You can't send messages to this channel";
export const buildClosedChatMessage = (email: string) => `${CLOSED_CHAT_BASE} ${email}`;

export const CLINICAL_ITEM_TYPES_THAT_SKIP_WARNING_MODAL = new Set<ClinicalItemType>([
  ClinicalItemType.DeathDeclarationTime,
  ClinicalItemType.BronchInSitu,
  ClinicalItemType.VisualInspection,
  ClinicalItemType.InSituVideoOfHeart,
  ClinicalItemType.DonorBiopsyReport,
  ClinicalItemType.LiverOnABackBench,
  ClinicalItemType.AnatomyPhotos,
  ClinicalItemType.ReconstructionVideosPhotos,
  ClinicalItemType.PFRatioInSitu,
  ClinicalItemType.PostFlushBronch,
  ClinicalItemType.PostFlushLungsPhoto,
  ClinicalItemType.VitalUpdates,
  ClinicalItemType.BronchVideoOnTheOCS,
  ClinicalItemType.OnOCSVideo,
  ClinicalItemType.PFRatioOnOCSDonorHospital,
  ClinicalItemType.PFRatioOnOCSTXCenter,
  ClinicalItemType.BloodSample,
  ClinicalItemType.FinalBronchVideoAtTheRecipientHospital,
  ClinicalItemType.FinalPhysicalExamPictureOrVideo,
  ClinicalItemType.OffOCSPhotoAndVideo,
  ClinicalItemType.NRPApplicabilityAndType,
  ClinicalItemType.DonorInOR,
  ClinicalItemType.RecordDelay,
  ClinicalItemType.OnSiteCoordinatorsContacts,
  ClinicalItemType.Heparin,
  ClinicalItemType.WithdrawalOfLifeSupport,
  ClinicalItemType.DonorDidNotExpireInTime,
  ClinicalItemType.SkinIncisionTime,
  ClinicalItemType.NRPStart,
  ClinicalItemType.CrossClampTimeFlushTime,
  ClinicalItemType.LiverAnatomyQuestionnaire,
  ClinicalItemType.LiverReconstructionQuestionnaire,
  ClinicalItemType.CardiacArrest,
  ClinicalItemType.PFOValveCondition,
  ClinicalItemType.OrganOnOCS,
  ClinicalItemType.LeavingDonorHospital,
  ClinicalItemType.ArrivalAtRecipientHospital,
  ClinicalItemType.OffOCS,
  ClinicalItemType.PerfusionTerminationOption,
  ClinicalItemType.FinalFlush,
  ClinicalItemType.AtDonorHospital,
  ClinicalItemType.WithdrawalOfLifeSupportTime,
  ClinicalItemType.Bile,
  ClinicalItemType.DryRun,
  ClinicalItemType.ScheduledDonorORStartTime,
  ClinicalItemType.OrganApproved,
  ClinicalItemType.OrganDeclined,
  ClinicalItemType.Randomization,
]);

export const MESSAGE_INPUT_MAX_ROWS = 10;

export const SHARED_CHAT_TYPES = [StreamChannelType.General, StreamChannelType.Quote, StreamChannelType.OpoGeneral];
